import React, { useMemo } from 'react';
import { CreditApplicationFieldsValues, formatMoney } from '@dill/dill-shared';
import { useAppSelector } from '../../../../redux/store';
import { SealCheck } from '@phosphor-icons/react';
import { COLORS } from '../../../../utils/colors';
import { Edit, Info } from 'react-feather';
import { DillButton } from '../../../../components/general/Dill/DillButton';

const EmailReference = ({
  field,
  onChangeTradeRefDetails,
}: {
  field: CreditApplicationFieldsValues;
  onChangeTradeRefDetails?: (
    type: 'email' | 'company',
    field: CreditApplicationFieldsValues
  ) => void;
}) => {
  const { creditApplication } = useAppSelector((state) => state.creditApplications);

  const emailReferenceDetails = useMemo(() => {
    if (field.status === 'Submitted') return null;
    if (!creditApplication) return null;

    const findValue = creditApplication.fieldValues.find(
      (f) =>
        f.id.toLowerCase().includes('trade-reference-company-name') &&
        f.groupedPosition === field?.groupedPosition &&
        f.groupStepPosition === field.groupStepPosition
    );

    if (!findValue) return null;

    const companyName = findValue.value.replaceAll(/[.,-]/g, ' ').trim().toUpperCase();
    const normalize = (s: string) => s.replaceAll(/[.,-]/g, ' ').trim().toUpperCase();

    const suggestedTradeReference = (creditApplication?.suggestedTradeReferences || []).find(
      (app) =>
        app &&
        (normalize(app.name).includes(companyName) || companyName.includes(normalize(app.name)))
    );
    const uploadedTradeReference = (creditApplication?.uploadedTradeReferences || []).find(
      (app) =>
        app &&
        (normalize(app.name).includes(companyName) || companyName.includes(normalize(app.name)))
    );

    const emails = suggestedTradeReference?.emails ?? [];

    const verifiedEmail = (emails ?? []).filter((e) => e.referenceStatus === 'verified');

    const uploadedEmail = uploadedTradeReference?.emails ?? [];
    const foundUploadedEmail = uploadedEmail.find(
      (e) => e.value.toLowerCase().trim() !== field.value.toLowerCase().trim()
    );
    let details = {};
    if (foundUploadedEmail && uploadedTradeReference) {
      details = {
        uploadeNotes: `Your uploaded trade reference suggests the following email: ${foundUploadedEmail.value}`,
      };
    }

    if (verifiedEmail?.length > 0) {
      const foundEmail = verifiedEmail.find(
        (e) => e.value.toLowerCase().trim() === field.value.toLowerCase().trim()
      );

      return {
        status: foundEmail ? 'verified' : 'dill-suggests',
        notes: foundEmail
          ? 'This email has been verified as a correct reference contact at this company.'
          : `Dill suggests a verified email for this reference company: ${verifiedEmail[0].value}`,
        ...details,
      };
    }

    const deliveredEmail = (emails ?? []).filter((e) => e.emailStatus === 'delivered');

    if (deliveredEmail?.length > 0) {
      const foundEmail = deliveredEmail.find(
        (e) => e.value.toLowerCase().trim() === field.value.toLowerCase().trim()
      );

      return {
        status: foundEmail ? 'delivered' : 'dill-suggests',
        notes: foundEmail
          ? ''
          : `Dill suggests to use email has been delivered but not verified: ${deliveredEmail[0].value}`,
        ...details,
      };
    }
    if (foundUploadedEmail && uploadedTradeReference) {
      return {
        status: 'upload-suggests',
        notes: '',
        uploadeNotes: `Your uploaded trade reference suggests the following email: ${foundUploadedEmail.value}`,
      };
    }

    return null;
  }, [creditApplication, field]);

  return (
    <div className="w-full">
      <div className="flex gap-2 items-center flex">
        <div className="text-xs truncate" data-private={field?.isHashed ? true : false}>
          {field.fromField === 'CURRENCY'
            ? formatMoney(field.value, (field as any).currencyDecimalPlaces ?? 2)
            : field.value}
        </div>
        {field?.sendEmail && field.status !== 'Submitted' && onChangeTradeRefDetails && (
          <DillButton
            variant="ghost-blue"
            icon={<Edit size={14} />}
            height={22}
            width={22}
            onClick={() =>
              onChangeTradeRefDetails(field.id.includes('email') ? 'email' : 'company', field)
            }
          />
        )}
      </div>
      {emailReferenceDetails && emailReferenceDetails?.notes && (
        <div className="flex items-center gap-2 mt-2 SUCCESS_500-BORDER CLR border SUCCESS_100-BG w-fit p-2 rounded-lg">
          <div data-tooltip-id={`email-reference-details ${field.uniqueId}`} className="flex">
            {emailReferenceDetails?.status === 'verified' && (
              <SealCheck size={16} weight="fill" color={COLORS.SUCCESS_500} />
            )}
            {emailReferenceDetails?.status === 'dill-suggests' && (
              <Info size={16} color={COLORS.SUCCESS_500} />
            )}
          </div>
          {emailReferenceDetails?.notes && (
            <span className="text-xs">{emailReferenceDetails?.notes}</span>
          )}
        </div>
      )}
      {emailReferenceDetails && emailReferenceDetails?.uploadeNotes && (
        <div className="flex items-center gap-2 mt-2 PRIMARY_500-BORDER border PRIMARY_100-BG w-fit p-2 rounded-lg">
          <div data-tooltip-id={`email-reference-details ${field.uniqueId}`} className="flex">
            <SealCheck size={16} weight="fill" color={COLORS.PRIMARY_500} />
          </div>
          {emailReferenceDetails?.uploadeNotes && (
            <span className="text-xs">{emailReferenceDetails?.uploadeNotes}</span>
          )}
        </div>
      )}
    </div>
  );
};

export default EmailReference;
